import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";

function buildNav(role) {
  // DASHBOARD_ADMIN sees only the OPD viewer — nothing else
  if (role === "DASHBOARD_ADMIN") {
    return [
      { to: "/admin/opd-viewer", end: false, icon: "📡", label: "OPD Live Viewer" },
    ];
  }

  const base = [
    { to: "/admin",          end: true,  icon: "📊", label: "Dashboard"       },
    { to: "/admin/requests", end: false, icon: "📋", label: "OPD / Requests"  },
    { to: "/admin/mrd",      end: false, icon: "🗂️", label: "Medical Records" },
    { to: "/admin/site",             end: false, icon: "🌐", label: "Website Content"  },
    { to: "/admin/doctor-profiles", end: false, icon: "👨‍⚕️", label: "Doctor Profiles"  },
    { to: "/admin/daily-posts",      end: false, icon: "📰", label: "Daily Posts"        },
    { to: "/admin/seasonal-regimen", end: false, icon: "🍃", label: "Seasonal Regimen"   },
  ];

  if (role === "MASTER_ADMIN") {
    return [
      ...base,
      { to: "/admin/opd-viewer",          end: false, icon: "📡", label: "OPD Live Viewer"      },
      { to: "/admin/online-consultation", end: false, icon: "💻", label: "Online Consultation"  },
      { to: "/admin/users",     end: false, icon: "👥", label: "Manage Users"      },
      { to: "/admin/doctors",   end: false, icon: "🩺", label: "Doctors"           },
      { to: "/admin/medicines", end: false, icon: "💊", label: "Medicine Database" },
      { to: "/admin/diseases",  end: false, icon: "🔬", label: "Disease Database"  },
      { to: "/admin/analytics", end: false, icon: "📈", label: "Analytics"         },
      { to: "/admin/whatsapp",  end: false, icon: "💬", label: "WhatsApp"           }, // ← NEW
      { to: "/admin/chatbot",   end: false, icon: "🤖", label: "Wellness Guide Bot" },
      { to: "/admin/settings",  end: false, icon: "⚙️", label: "Settings"          },
    ];
  }

  return [
    ...base,
    { to: "/admin/settings", end: false, icon: "⚙️", label: "Settings" },
  ];
}

function roleBadgeLabel(role) {
  const map = {
    MASTER_ADMIN:    "Master Admin",
    ADMIN:           "Admin",
    CONTENT:         "Content",
    DOCTOR:          "Doctor",
    STAFF:           "Staff",
    DASHBOARD_ADMIN: "Dashboard Admin",
  };
  return map[role] || role;
}

export default function AdminLayout({ children }) {
  const navigate    = useNavigate();
  const location    = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const admin = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("adminUser") || "null"); }
    catch { return null; }
  }, []);

  const logout = () => {
    sessionStorage.removeItem("adminToken");
    sessionStorage.removeItem("adminUser");
    navigate("/clinic-management/signin", { replace: true });
  };

  if (!admin) {
    navigate("/clinic-management/signin", { replace: true });
    return null;
  }

  const navItems = buildNav(admin.role);

  const SidebarInner = ({ onNav }) => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 bg-green-600 rounded-xl flex items-center justify-center text-white text-lg">🏥</div>
          <div>
            <p className="text-sm font-bold text-gray-800 leading-tight">Clinic Admin</p>
            <p className="text-xs text-green-600 font-medium">
              {roleBadgeLabel(admin.role)}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400 truncate">{admin.name}</p>
        {admin.section && <p className="text-xs text-gray-300">{admin.section}</p>}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive ? "bg-green-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
              }`
            }
          >
            <span>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-gray-100">
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition"
        >
          🚪 Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-gray-50">

      <aside className="hidden md:flex w-60 bg-white border-r border-gray-100 flex-col shrink-0">
        <SidebarInner onNav={() => {}} />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 w-60 bg-white z-50 flex flex-col md:hidden shadow-xl"
            >
              <SidebarInner onNav={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto" style={{ height:"100vh" }}>
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
          <button onClick={() => setMobileOpen(true)} className="text-xl text-gray-600">☰</button>
          <span className="font-semibold text-green-700 text-sm">Clinic Admin</span>
          <button onClick={logout} className="text-xs text-red-500">Logout</button>
        </div>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex-1 p-6 max-w-7xl mx-auto w-full"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}